import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { messagingSectionCountersQueryKey } from '@/hooks/queries/use-messaging-section-counters-query';
import api from '@/libs/axiosInstance';
import type {
  ApiErrorResponse,
  ApiResponse,
  BulkWishlistOutreachRequest,
  BulkWishlistOutreachResponse,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';

/**
 * Sends one bulk prospection email to several wishlisted creators in a single
 * call to `POST /dashboard/wishlist/outreach-email/bulk`. The response returns a
 * per-recipient recap (`queued` / `skipped_no_email` / `failed`) that the caller
 * renders; there is no client-side fan-out.
 *
 * Only a total request failure toasts here — per-recipient outcomes are not
 * errors, they belong in the recap surface.
 */
export function useSendBulkCreatorOutreachEmail() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<
    BulkWishlistOutreachResponse,
    AxiosError<ApiErrorResponse>,
    BulkWishlistOutreachRequest
  >({
    mutationFn: async ({
      wishlistItemIds,
      subject,
      body,
      attachmentIds,
    }): Promise<BulkWishlistOutreachResponse> => {
      const response = await api.post<ApiResponse<BulkWishlistOutreachResponse>>(
        `/dashboard/wishlist/outreach-email/bulk`,
        {
          wishlistItemIds,
          subject,
          body,
          attachmentIds,
        }
      );

      if (!response.data.data) {
        throw new Error('Invalid response structure');
      }

      return response.data.data;
    },
    onSuccess: () => {
      if (user?.currentBrandId) {
        queryClient.invalidateQueries({
          queryKey: messagingSectionCountersQueryKey(user.currentBrandId),
        });
      }
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
    },
    onError: (error) => {
      // A plan refusal (a prospect whose address the plan withholds) opens the
      // upsell from the API client; a toast would repeat it.
      if (isPlanLimitError(error)) return;
      const errorCode = error.response?.data?.code;
      const fallback = error.response?.data?.message ?? t('creatorOutreach.send.error.generic');
      const translated = errorCode ? t(`errors.${errorCode}`, { defaultValue: '' }) : '';

      toaster.create({
        title: t('creatorOutreach.send.error.title'),
        description: translated || fallback,
        type: 'error',
        duration: 6000,
      });
    },
  });
}
